import { H } from "./place";
import type { Where } from "./place";
import type { Feel } from "./auto";

// What moves on the table, and how (design 10 §7): pure, as place() is. place() says where every tile is; this compares
// that with where it was and plans the flights; ui/Flight plays them. Nothing here touches the page.

/** Where a sprite's anchor is on the screen (in the board's pixels), its turn and its tile's width. */
export interface Spot {
  x: number;
  y: number;
  angle: number;
  width: number;
}

/** A tile at rest: its spot, the picture it shows and where in the game that is. */
export interface Stand extends Spot {
  pic: string | null;
  where: Where;
  seat: number;
}

/** How a move feels: its length by distance, its curve, and how much the tile rises (grows a little) at the middle of
 *  its flight. Weighty: a firm start and a long, quiet settle, no bounce. */
export interface FeelSpec {
  base: number;
  perPx: number;
  max: number;
  ease: readonly [number, number, number, number];
  lift: number; // how much the tile grows at the middle of its flight, as if raised
  arc: number; // how far its path bows from the straight line, over the distance travelled
}
// Each tile speeds up out of its place and slows into the next (an ease in and out, not a glide), along a
// slight arc, lifted as it travels (owner, 2026-10-13: "more life").
export const FEEL: Record<Feel, FeelSpec | null> = {
  weighty: { base: 240, perPx: 0.34, max: 460, ease: [0.62, 0, 0.32, 1], lift: 0.08, arc: 0.12 },
  brisk: { base: 160, perPx: 0.22, max: 300, ease: [0.5, 0, 0.25, 1], lift: 0.05, arc: 0.08 },
  gentle: { base: 340, perPx: 0.44, max: 600, ease: [0.6, 0, 0.4, 1], lift: 0.07, arc: 0.1 },
  instant: null,
};

/** A block of the deal after the one before it, in milliseconds. */
export const DEAL_STEP_MS = 70;
/** The last dealt tile's place in the deal's order. The server waits the deal out before anything follows it (kDealMs,
 *  mjcore/server/table.h): at every feel, this block's flight has landed by then. */
export const LAST_DEALT = 15;
/** The deal's order of a tile already drawn when the table comes: after the dealt tiles. */
const AFTER_THE_DEAL = LAST_DEALT + 1;
/** Many tiles new at once, in a hand already on the table: another seat's view of it, not a move of the game. */
const MANY_NEW = 20;

/** Why the table changed:
 *  deal: a hand first drawn in its first go-around; its tiles come out of the wall.
 *  arrive: a table first drawn later than that, or seen anew: its new tiles are simply there.
 *  relaid: the measured places changed (a resize, the fonts arriving): every tile is simply at its new place.
 *  play: the game moved. */
export type Cause = "deal" | "arrive" | "relaid" | "play";

export function causeOf(seen: number, fresh: number, dealing: boolean, relaid: boolean): Cause {
  if (seen === 0) return dealing ? "deal" : "arrive";
  if (relaid) return "relaid";
  return fresh > MANY_NEW ? "arrive" : "play";
}

/** A tile as place() has it now: `wall`, where the deal takes it from, and `deal`, its place in the deal's order. */
export interface Now<T extends Stand> {
  key: string;
  at: T;
  wall?: T;
  deal?: number;
}

/** What a dealt tile's flight set out with. */
export interface DealFlight<T extends Stand> {
  from: T;
  ms: number;
  delay: number;
}

/** A flight under way: where its tile is now and, in the deal, what it set out with. */
export interface Aloft<T extends Stand> {
  pose: Spot;
  deal?: DealFlight<T>;
}

/** The kinds of move:
 *  deal: out of the wall, in the deal's order.
 *  aim: a tile the deal is still bringing, its place changed: the same flight, landing elsewhere.
 *  leave: out of a hand or a river, to a river or a meld.
 *  settle: within a hand, closing up behind what left it, once that has landed (design 10 T11).
 *  go: any other, from one place to another.
 *  drop: a tile new to the table comes down where it lies. */
export type MoveKind = "deal" | "aim" | "leave" | "settle" | "go" | "drop";

export interface Move<T extends Stand> {
  key: string;
  kind: MoveKind;
  to: T;
  from: T | null;
  ms: number;
  delay: number;
  turn: boolean; // it shows `from`'s picture for its first half, `to`'s for its second
}

const held = (w: Where) => w === "hand" || w === "drawn";
const laid = (w: Where) => w === "river" || w === "meld";
const samePlace = (a: Stand, b: Stand) => Math.abs(a.x - b.x) < 0.5 && Math.abs(a.y - b.y) < 0.5 && a.angle === b.angle && Math.abs(a.width - b.width) < 0.1 && a.pic === b.pic;

/** The flights from `before` to `tiles`. `aloft` tells of a tile's flight under way. `landed`: the tiles that reached a
 *  river or a meld without a flight, the feel having none. */
export function planMoves<T extends Stand>(tiles: readonly Now<T>[], before: ReadonlyMap<string, T>, cause: Cause, spec: FeelSpec | null, aloft: (key: string) => Aloft<T> | undefined): { moves: Move<T>[]; landed: Now<T>[] } {
  const moves: Move<T>[] = [];
  const landed: Now<T>[] = [];
  if (cause === "relaid") return { moves, landed };
  const length = (a: Spot, b: Spot) => (spec ? Math.max(spec.base, Math.min(spec.max, spec.base + spec.perPx * Math.hypot(b.x - a.x, b.y - a.y))) : 0);
  // A discard or call plays in order (design 10 T11): what leaves a hand for the river or a meld goes
  // first; the hand it left closes behind it once it has landed.
  const lead = new Map<number, number>(); // seat: the longest leaving move's length
  for (const t of tiles) {
    const to = t.at;
    const old = before.get(t.key);
    if (!old) {
      if (!spec || to.where === "wall" || cause === "arrive") continue; // the wall is simply there
      if (cause === "deal" && t.wall && held(to.where)) {
        const ms = length(t.wall, to);
        moves.push({ key: t.key, kind: "deal", to, from: t.wall, ms, delay: (t.deal ?? AFTER_THE_DEAL) * DEAL_STEP_MS, turn: t.wall.pic !== to.pic });
      } else moves.push({ key: t.key, kind: "drop", to, from: null, ms: 150, delay: 0, turn: false });
      continue;
    }
    if (samePlace(old, to)) continue;
    const air = aloft(t.key);
    if (air?.deal && held(to.where)) {
      const { from, ms, delay } = air.deal;
      moves.push({ key: t.key, kind: "aim", to, from, ms, delay, turn: from.pic !== to.pic });
      continue;
    }
    if (!spec) {
      if (laid(to.where) && old.where !== to.where) landed.push(t);
      continue;
    }
    // An interrupted move continues from where the tile is now, as its new picture.
    const from = air ? { ...old, ...air.pose } : old;
    const ms = length(from, to);
    const leaving = (held(old.where) || old.where === "river") && laid(to.where);
    if (leaving) lead.set(to.seat, Math.max(lead.get(to.seat) ?? 0, ms));
    moves.push({ key: t.key, kind: leaving ? "leave" : held(old.where) && held(to.where) ? "settle" : "go", to, from, ms, delay: 0, turn: !air && from.pic !== to.pic });
  }
  for (const m of moves) if (m.kind === "settle") m.delay = lead.get(m.to.seat) ?? 0;
  return { moves, landed };
}

/** cubic-bezier(x1, y1, x2, y2) at progress t, by bisection on x. */
function bezier(t: number, [x1, y1, x2, y2]: FeelSpec["ease"]): number {
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 24; i++) {
    const s = (lo + hi) / 2;
    const x = 3 * (1 - s) ** 2 * s * x1 + 3 * (1 - s) * s * s * x2 + s ** 3;
    if (x < t) lo = s;
    else hi = s;
  }
  const s = (lo + hi) / 2;
  return 3 * (1 - s) ** 2 * s * y1 + 3 * (1 - s) * s * s * y2 + s ** 3;
}

/** The curve at each of a flight's n steps, worked out once per length: a deal asks for it 53 times in one task, on a phone. */
const curves = new Map<string, number[]>();
function curve(n: number, ease: FeelSpec["ease"]): number[] {
  const key = `${n}|${ease.join()}`;
  let c = curves.get(key);
  if (!c) {
    c = [];
    for (let i = 0; i <= n; i++) c.push(i === 0 ? 0 : i === n ? 1 : bezier(i / n, ease));
    curves.set(key, c);
  }
  return c;
}

/** One step of a flight: where the tile is, and whether it still shows the picture it set out with. */
export interface Pose extends Spot {
  early: boolean;
  opacity?: number; // a tile dropped in place fades in as it comes down
}

/** Every step of a flight, one per frame of the budget `fps`: from `from` to `to` along a slight arc, lifted as it
 *  travels; with no `from`, dropped in place. Each step is held until the next. */
export function flightPath(from: Spot | null, to: Spot, ms: number, turn: boolean, spec: FeelSpec, fps: number): Pose[] {
  const n = Math.max(2, Math.round((ms / 1000) * fps));
  const eased = curve(n, spec.ease);
  const out: Pose[] = [];
  for (let i = 0; i <= n; i++) {
    const e = eased[i]!;
    const early = turn && i / n < 0.5;
    if (!from) {
      out.push({ ...to, y: to.y - 0.35 * to.width * H * (1 - e), opacity: e, early });
    } else if (i === n) {
      out.push({ x: to.x, y: to.y, angle: to.angle, width: to.width, early });
    } else {
      // The path bows away from the straight line, always towards the top of the screen.
      const bow = Math.sin(Math.PI * e); // 0 at both ends, 1 halfway along
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const d = Math.hypot(dx, dy) || 1;
      const side = dx / d >= 0 ? 1 : -1;
      const off = spec.arc * d * bow;
      out.push({
        x: from.x + dx * e + (dy / d) * off * side,
        y: from.y + dy * e - (Math.abs(dx) / d) * off,
        angle: from.angle + (to.angle - from.angle) * e,
        width: (from.width + (to.width - from.width) * e) * (1 + spec.lift * bow),
        early,
      });
    }
  }
  return out;
}

/** Where a flight's tile is `time` milliseconds after it was set going: a flight is a held step at a time. */
export function poseAt(path: readonly Pose[], ms: number, delay: number, time: number): Pose {
  const n = path.length - 1;
  const i = Math.floor(((time - delay) / ms) * n);
  return path[Math.max(0, Math.min(n, i))]!;
}
